import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { usersService } from '../../services/membersService';
import { useImageUrl } from '../../hooks/useImageUrl';
import { Avatar } from '../ui/Avatar';
import { Logo } from '../ui/Logo';
import { navigationConfig } from '../../config/navigation';
import { filterNavGroups } from '../../utils/navigation';
import { DesktopNav } from './navigation/DesktopNav';
import { MobileNavPanel } from './navigation/MobileNavPanel';

interface UnifiedHeaderProps {
  variant?: 'default' | 'heritage';
  currentPage?: string;
}

const UnifiedHeader: React.FC<UnifiedHeaderProps> = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  const { data: userWithMember } = useQuery({
    queryKey: ['user-with-member', user?.id],
    queryFn: () => usersService.getUserWithMember(user!.id),
    enabled: isAuthenticated && !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
  const profileImageUrl = useImageUrl(userWithMember?.familyMember?.profileImage);

  const filteredGroups = filterNavGroups(navigationConfig, isAuthenticated, user?.role);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <Logo size="sm" />
              <div>
                <h1 className="text-base font-semibold text-text-primary">
                  The Redd Family
                </h1>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <DesktopNav groups={filteredGroups} />

            {/* Desktop Auth */}
            <div className="hidden lg:flex items-center space-x-3">
              {isAuthenticated && user ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <Avatar
                      firstName={user.firstName}
                      lastName={user.lastName}
                      src={profileImageUrl}
                      size="sm"
                      className="border-0"
                    />
                    <span className="text-sm text-text-secondary hover:text-brand transition-colors">
                      {user.firstName} {user.lastName}
                    </span>
                  </Link>
                  <button
                    onClick={logout}
                    className="bg-brand text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-brand-hover transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="bg-brand text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-brand-hover transition-colors"
                >
                  Login
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-text-secondary p-2 hover:bg-surface-1 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Panel */}
      <MobileNavPanel
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        groups={filteredGroups}
      />
    </>
  );
};

export { UnifiedHeader };
